import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';

/**
 * Home Page weather card. Pulls the latest entry from
 * GET /api/v1/tenants/me/weather/recent?hours=1 (the same endpoint that
 * powers the PWA's offline weather cache and the Spray Module's history
 * dashboard) and renders it with a Bootstrap-icon condition glyph plus
 * the raw readouts for temperature, wind, precipitation, humidity, and
 * pressure.
 *
 * The endpoint is bound to the authenticated tenant's centralLocation —
 * i.e. the "Farm Location" set in Settings — which satisfies the spec's
 * "weather based on Farm Location" requirement without needing a
 * client-side coordinate lookup.
 *
 * Track: spray_weather_enhancements_20260429 / Phase 1.
 */

interface WeatherEntry {
  timestamp: string;
  temp?: number;
  windSpeed?: number;
  precipitation?: number;
  condition?: string;
  humidity?: number;
  pressure?: number;
}

interface RecentWeatherResponse {
  entries: WeatherEntry[];
  hours: number;
}

/**
 * Map a free-text WeatherAPI condition into a Bootstrap Icons class. The
 * source strings come from WeatherAPI ("Sunny", "Partly cloudy", "Light
 * rain", "Thunderstorm", etc.); we match on substrings so minor wording
 * variants ("Heavy rain at times" → rain) still resolve to a sensible
 * icon. Unknown / missing conditions fall back to the generic cloud-sun
 * icon so the card never renders without a glyph.
 */
export function conditionToIcon(condition?: string): string {
  if (!condition) return 'bi-cloud-sun';
  const c = condition.toLowerCase();
  if (c.includes('thunder')) return 'bi-cloud-lightning-rain';
  if (c.includes('snow') || c.includes('sleet') || c.includes('blizzard')) return 'bi-cloud-snow';
  if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return 'bi-cloud-rain';
  if (c.includes('fog') || c.includes('mist') || c.includes('haze')) return 'bi-cloud-fog';
  if (c.includes('overcast')) return 'bi-clouds';
  if (c.includes('partly') || c.includes('cloud')) return 'bi-cloud-sun';
  if (c.includes('clear') || c.includes('sun')) return 'bi-sun';
  return 'bi-cloud-sun';
}

const formatNumber = (n?: number): string =>
  typeof n === 'number' ? `${n}` : '—';

const WeatherDisplay: React.FC = () => {
  const { t } = useTranslation();
  const [latest, setLatest] = useState<WeatherEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHasError(false);
    setEmpty(false);

    axios
      .get<RecentWeatherResponse>('/api/v1/tenants/me/weather/recent', {
        params: { hours: 1 },
        withCredentials: true,
      })
      .then(res => {
        if (cancelled) return;
        const entries = res.data?.entries || [];
        if (entries.length === 0) {
          setEmpty(true);
          setLatest(null);
          return;
        }
        const newest = entries.reduce((acc, cur) =>
          Date.parse(cur.timestamp) > Date.parse(acc.timestamp) ? cur : acc
        );
        setLatest(newest);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="weather-display weather-display--loading" data-testid="weather-display-loading">
        <Spinner animation="border" size="sm" role="status" />
        <span>{t('home.weather.loading')}</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="weather-display weather-display--error" data-testid="weather-display-error">
        {t('home.weather.error')}
      </div>
    );
  }

  if (empty || !latest) {
    return (
      <div className="weather-display weather-display--empty" data-testid="weather-display-empty">
        {t('home.weather.empty')}
      </div>
    );
  }

  const iconClass = conditionToIcon(latest.condition);

  return (
    <div className="weather-display" data-testid="weather-display">
      <div className="d-flex align-items-center gap-3 mb-2">
        <i
          className={`bi ${iconClass}`}
          data-testid="weather-display-icon"
          style={{ fontSize: '2.5rem' }}
          aria-hidden="true"
        />
        <div>
          <div className="h5 mb-0">{t('home.weather.title')}</div>
          {latest.condition && (
            <div className="text-muted small">{latest.condition}</div>
          )}
        </div>
      </div>
      <div className="d-flex flex-wrap gap-3 small">
        <span data-testid="weather-display-temp">
          <strong>{t('home.weather.temp')}:</strong> {formatNumber(latest.temp)} °C
        </span>
        <span data-testid="weather-display-wind">
          <strong>{t('home.weather.wind')}:</strong> {formatNumber(latest.windSpeed)} kph
        </span>
        <span data-testid="weather-display-precip">
          <strong>{t('home.weather.precip')}:</strong> {formatNumber(latest.precipitation)} mm
        </span>
        <span data-testid="weather-display-humidity">
          <strong>{t('home.weather.humidity')}:</strong> {formatNumber(latest.humidity)} %
        </span>
        <span data-testid="weather-display-pressure">
          <strong>{t('home.weather.pressure')}:</strong> {formatNumber(latest.pressure)} mb
        </span>
      </div>
    </div>
  );
};

export default WeatherDisplay;
